import React, { useMemo } from 'react';
import { Building2, Pickaxe } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import {
    useGetSiteFilterOptionsQuery,
    useGetPortfolioRiskStatsQuery,
    useGetPortfolioMaterialityBreakdownQuery,
    type GqlRiskBandLevel,
    type GqlSonBandLevel,
} from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import { useGetRiskMaterialityFactorsQuery } from 'src/shared/state/api-slices/riskMaterialityCmsSlice';
import RiskDriversSection from 'src/shared/components/risk-drivers/RiskDriversSection';
import useGnrFilters from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrFilters';
import { sanitizeSelectedFilters } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import ImpactsAndDependenciesInfoBlock from 'src/shared/components/impacts-and-dependencies-info-block/ImpactsAndDependenciesInfoBlock';
import RankCard from 'src/shared/components/rank-card/RankCard';
import GnrActiveFilters from '../site-list/components/filters/GnrActiveFilters';
import SiteListFilters from '../site-list/components/filters/SiteListFilters';

const FEATURE_ID = 'risk-insights';

const GnrImpactsAndDependencies = () => {
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });
    const { currentCustomerId } = useCustomer();
    const { selectedFilters, applyFilters, resetFilters, updateFilters } = useGnrFilters();

    const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetSiteFilterOptionsQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );

    const siteFilterOptions = filterOptionsData?.organisation?.siteFilterOptions;

    const validatedFilters = useMemo(
        () => sanitizeSelectedFilters(selectedFilters, siteFilterOptions),
        [selectedFilters, siteFilterOptions]
    );

    const isicFilter = validatedFilters.find(f => f.name === 'isic')?.values;
    const countryFilter = validatedFilters.find(f => f.name === 'country')?.values;
    const physicalRiskLevels = validatedFilters.find(f => f.name === 'physical_risk_levels')?.values as GqlRiskBandLevel[] | undefined;
    const dependencyLevels = validatedFilters.find(f => f.name === 'dependency_levels')?.values as GqlRiskBandLevel[] | undefined;
    const impactLevels = validatedFilters.find(f => f.name === 'impact_levels')?.values as GqlRiskBandLevel[] | undefined;
    const sonLevels = validatedFilters.find(f => f.name === 'son_levels')?.values as GqlSonBandLevel[] | undefined;

    const filterArgs = {
        isic: isicFilter,
        countries: countryFilter,
        physicalRiskLevels,
        dependencyLevels,
        impactLevels,
        sonLevels,
    };

    const { data: portfolioStatsData, isLoading: isLoadingStats } = useGetPortfolioRiskStatsQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const { data: materialityData, isLoading: isLoadingMateriality } = useGetPortfolioMaterialityBreakdownQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const { data: riskMaterialityFactors = [] } = useGetRiskMaterialityFactorsQuery();

    const stats = portfolioStatsData?.organisation?.portfolioRiskStats;

    const materialityBreakdown = materialityData?.organisation?.portfolioRiskStats?.materialityBreakdown ?? [];

    const cmsDescriptionMap = useMemo(
        () => Object.fromEntries(riskMaterialityFactors.map(f => [f.sourceKey, f.description])),
        [riskMaterialityFactors]
    );

    const dependencyItems = useMemo(
        () =>
            materialityBreakdown
                .filter(m => m.materialityType === 'Dependency')
                .map(m => ({ name: m.name, score: m.score, description: cmsDescriptionMap[m.sourceKey] ?? null })),
        [materialityBreakdown, cmsDescriptionMap]
    );

    const impactItems = useMemo(
        () =>
            materialityBreakdown
                .filter(m => m.materialityType === 'Impact')
                .map(m => ({ name: m.name, score: m.score, description: cmsDescriptionMap[m.sourceKey] ?? null })),
        [materialityBreakdown, cmsDescriptionMap]
    );

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    return (
        <StandardAppLayout
            title='Impacts & Dependencies of nature'
            subTitle='How your operations impact and depend on nature'
            classNames={{ otherActions: '!justify-start !p-0' }}
            stickyHeader={false}
            otherActions={
                <SiteListFilters
                    selectedFilters={selectedFilters}
                    onApply={applyFilters}
                    onClear={resetFilters}
                    filterOptions={siteFilterOptions}
                    isLoadingFilterOptions={isLoadingFilterOptions}
                    businessCentralId={currentCustomerId ?? undefined}
                />
            }
            mainContent={
                <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='gnr-impacts-and-dependencies-main'>
                    <GnrActiveFilters
                        selectedFilters={validatedFilters}
                        countries={siteFilterOptions?.countries}
                        isicCodes={siteFilterOptions?.isicCodes}
                        onRemove={updateFilters}
                        onClearAll={resetFilters}
                        matchingSiteCount={stats?.assessedSiteCount}
                    />

                    <div className='grid grid-cols-2 gap-4'>
                        {/* Dependencies column */}
                        <div className='flex flex-col gap-1'>
                            <ImpactsAndDependenciesInfoBlock
                                title='Dependencies'
                                icon={Building2}
                                slug='dependencies-te'
                                description='Evaluates reliance on ecosystem services such as water provision, climate regulation, and pollination that are critical for business operations and may be at risk due to environmental degradation.'
                            />
                            <div className='flex flex-row gap-1'>
                                <div className='flex-1 flex flex-col'>
                                    <RiskScoreCard
                                        title='Dependencies'
                                        value={stats?.meanOperationalDependencies ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoadingStats}
                                        size='md'
                                        slug='dependencies'
                                    />
                                </div>
                                <RankCard
                                    title='High Dependency sites'
                                    rank={stats?.highDependencySiteCount?.toString() ?? null}
                                    total={stats?.assessedSiteCount ?? null}
                                    isLoading={isLoadingStats}
                                    slug='high-dependency-sites'
                                    className='flex-1'
                                    variant='inline'
                                    subtitle='Dependency score > 0.60'
                                />
                            </div>
                        </div>

                        {/* Impact on Nature column */}
                        <div className='flex flex-col gap-1'>
                            <ImpactsAndDependenciesInfoBlock
                                title='Impact on Nature'
                                icon={Pickaxe}
                                slug='impact-on-nature-te'
                                description='Measures the negative effects of business operations on nature, including pollution, habitat destruction, resource depletion, and greenhouse gas emissions that contribute to ecosystem degradation.'
                            />
                            <div className='flex flex-row gap-1'>
                                <div className='flex-1 flex flex-col'>
                                    <RiskScoreCard
                                        title='Impact on Nature'
                                        value={stats?.meanImpactsOnNature ?? null}
                                        scoreType='Risk'
                                        isLoading={isLoadingStats}
                                        size='md'
                                        slug='impact-on-nature'
                                    />
                                </div>
                                <RankCard
                                    title='High Impact sites'
                                    rank={stats?.highImpactSiteCount?.toString() ?? null}
                                    total={stats?.assessedSiteCount ?? null}
                                    isLoading={isLoadingStats}
                                    slug='high-impact-on-nature-sites'
                                    className='flex-1'
                                    variant='inline'
                                    subtitle='Impact score > 0.60'
                                />
                            </div>
                        </div>
                    </div>

                    <RiskDriversSection dependencyItems={dependencyItems} impactItems={impactItems} isLoading={isLoadingMateriality} />
                </div>
            }
        />
    );
};

export default GnrImpactsAndDependencies;
